import {match, Pattern} from 'ts-pattern';

interface FormattedInspectionCommentParagraph {
  header?: string;
  body: string;
}

const NON_BREAKING_SPACE = '\u00A0';
const CZECH_LANGUAGE_PREFIX = 'cs';
const PARAGRAPH_SEPARATOR = /\n[\t ]*\n+/u;
const CEBIA_INLINE_HEADER =
  /([^\n\p{L}])((?:\p{Lu}[\p{Lu}\p{M}\d /&()+-]*|\p{Lu}[\p{L}\p{M}\d /&+-]*\([\p{L}\p{M}\d /&+-]+\))[\t ]*:)/gu;
const CEBIA_INLINE_HEADER_REPLACEMENT = '$1\n\n$2';
const INSPECTION_HEADER =
  /^((?:(?=[^\n:]*\p{Lu})[\p{Lu}\p{M}\d /&()+-]+|\p{Lu}[\p{L}\p{M}\d /&+-]*\([\p{L}\p{M}\d /&+-]+\))[\t ]*:)([\s\S]*)$/u;
const NUMBER_RANGE = /(?<![\d.,])(\d[\d.,]*)[\t ]+(bis|–)[\t ]+(\d[\d.,]*)/giu;
const NUMBER_WITH_UNIT =
  /(?<![\d.,])(\d[\d.,]*)[\t ]+(km|µm|μm|mm|cm|m|kg|g|l|kW|PS|hp|°C|%)(?=\b|[\t .,;:!?)]|$)/gu;
const CZECH_ONE_LETTER_WORD = /(?<!\p{L})([ksvzaiou])[\t ]+(?=\S)/giu;

const splitCebiaInlineHeaders = (plaintext: string, shouldSplitInlineHeaders: boolean) =>
  match(shouldSplitInlineHeaders)
    .with(true, () => plaintext.replace(CEBIA_INLINE_HEADER, CEBIA_INLINE_HEADER_REPLACEMENT))
    .with(false, () => plaintext)
    .exhaustive();

const formatLocaleSpecificSpaces = (text: string, locale?: string) =>
  match(locale?.toLowerCase().startsWith(CZECH_LANGUAGE_PREFIX) === true)
    .with(true, () => text.replace(CZECH_ONE_LETTER_WORD, `$1${NON_BREAKING_SPACE}`))
    .with(false, () => text)
    .exhaustive();

const formatNonBreakingSpaces = (text: string, locale?: string) => {
  const textWithRanges = text.replace(
    NUMBER_RANGE,
    `$1${NON_BREAKING_SPACE}$2${NON_BREAKING_SPACE}$3`
  );
  const textWithUnits = textWithRanges.replace(NUMBER_WITH_UNIT, `$1${NON_BREAKING_SPACE}$2`);

  return formatLocaleSpecificSpaces(textWithUnits, locale);
};

const formatParagraph = (
  paragraph: string,
  locale?: string
): FormattedInspectionCommentParagraph => {
  const headerMatch = INSPECTION_HEADER.exec(paragraph);

  return match(headerMatch)
    .with(Pattern.nullish, () => ({body: formatNonBreakingSpaces(paragraph, locale)}))
    .otherwise((result) => ({
      header: result[1],
      body: formatNonBreakingSpaces(result[2], locale),
    }));
};

export function formatInspectionComment(
  plaintext: string,
  locale?: string,
  shouldSplitInlineHeaders = false
): FormattedInspectionCommentParagraph[] {
  return splitCebiaInlineHeaders(plaintext, shouldSplitInlineHeaders)
    .replace(/\r\n?/gu, '\n')
    .split(PARAGRAPH_SEPARATOR)
    .map((paragraph) => formatParagraph(paragraph, locale));
}
